import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const ProductCard = ({ product, viewMode = 'grid' }) => {
  const { addToCart, loading } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const success = await addToCart(product._id, 1);
    if (success) {
      toast({
        title: "Added to cart!",
        description: `${product.name} has been added to your cart.`,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (viewMode === 'list') {
    return (
      <Link
        to={`/product/${product.slug}`}
        className="bg-white overflow-hidden shadow-lg hover:shadow-xl transition-all border-l-4 border-amber-600 flex"
        data-testid={`product-card-${product._id}`}
      >
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-48 h-48 object-cover"
        />
        <div className="flex-1 p-6 flex justify-between items-center">
          <div>
            <div className="text-xs text-amber-600 mb-1 uppercase tracking-wider font-medium">{product.category}</div>
            <h3 className="font-bold text-xl text-stone-900 mb-2">{product.name}</h3>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(product.rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-stone-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-stone-600">({product.review_count})</span>
            </div>
            <p className="text-stone-600 line-clamp-2">{product.description}</p>
          </div>
          <div className="text-right">
            <div className="mb-4">
              <div className="text-3xl font-black text-stone-900">${product.price}</div>
              {product.original_price && (
                <div className="text-sm text-stone-500 line-through">${product.original_price}</div>
              )}
            </div>
            <Button
              onClick={handleAddToCart}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700 w-full rounded-none"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Add to Cart
            </Button>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/product/${product.slug}`}
      className="bg-white overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
      data-testid={`product-card-${product._id}`}
    >
      {/* Product Image */}
      <div className="relative aspect-square overflow-hidden bg-stone-100">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        {/* Kilim-style corner accent */}
        <div className="absolute top-0 right-0 w-12 h-12"
          style={{
            background: 'linear-gradient(135deg, transparent 50%, rgba(217,119,6,0.9) 50%)'
          }}
        ></div>
        {product.badge && (
          <Badge
            className={`absolute top-3 left-3 rounded-none ${
              product.badge === 'Best Seller'
                ? 'bg-amber-600'
                : product.badge === 'New'
                ? 'bg-red-600'
                : product.badge === 'Sale'
                ? 'bg-red-700'
                : product.badge === 'Eco'
                ? 'bg-green-600'
                : 'bg-orange-600'
            } text-white border-0`}
          >
            {product.badge}
          </Badge>
        )}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-stone-900/60 flex items-center justify-center">
            <span className="text-white font-bold text-lg">Out of Stock</span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-5 border-t-2 border-amber-600">
        <div className="text-xs text-amber-600 mb-2 uppercase tracking-wider font-medium">{product.category.replace('-', ' ')}</div>
        <h3 className="font-bold text-stone-900 mb-3 line-clamp-2 min-h-[3rem]">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-4 w-4 ${
                  i < Math.floor(product.rating)
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-stone-300'
                }`}
              />
            ))}
          </div>
          <span className="text-sm text-stone-600">({product.review_count})</span>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-baseline space-x-2">
            <span className="text-2xl font-black text-stone-900">
              ${product.price}
            </span>
            {product.original_price && (
              <span className="text-sm text-stone-500 line-through">
                ${product.original_price}
              </span>
            )}
          </div>
        </div>

        {/* Add to Cart Button */}
        <Button
          onClick={handleAddToCart}
          disabled={loading || product.stock === 0}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-none transition-colors"
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
        </Button>
      </div>
    </Link>
  );
};

export default ProductCard;
